import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Badge, Dot, Btn, Spinner } from '../ui.jsx';
import { CoachPanel } from '../widgets/CoachPanel.jsx';
import { WatchPanel } from '../widgets/WatchPanel.jsx';
import { QuickStartCard } from '../widgets/QuickStartCard.jsx';
import { Safe } from '../widgets/ErrorBoundary.jsx';
import { useTerminology } from '../hooks/useTerminology.js';

const STAGE_COLOR = {
  'New Lead': G.cyan, 'Contacted': G.blue, 'Qualified': G.purple,
  'Estimate Sent': G.yellow, 'Booked': G.green, 'In Progress': G.orange,
  'Invoiced': G.pink, 'Paid': G.green, 'Lost': G.red,
};

export function Dashboard({ user, go, toast }) {
  const { cap, plural } = useTerminology();
  const [data, setData] = useState({ contacts:[], jobs:[], invoices:[], estimates:[], kpis:null });
  const [loading, setLoading] = useState(true);
  const [predictive, setPredictive] = useState([]);
  const [setup, setSetup] = useState(null);
  const [hideSetup, setHideSetup] = useState(()=>localStorage.getItem('tradeops:hideSetup') === '1');

  useEffect(() => {
    Promise.all([
      api('/api/contacts').catch(()=>[]),
      api('/api/jobs').catch(()=>[]),
      api('/api/invoices').catch(()=>[]),
      api('/api/estimates').catch(()=>[]),
      api('/api/kpis').catch(()=>null),
    ]).then(([contacts, jobs, invoices, estimates, kpis]) => {
      setData({ contacts, jobs, invoices, estimates, kpis });
      setLoading(false);
    });
    // Auto-scan + load predictive alerts
    api('/api/growth/predictive/scan', { method:'POST' }).then(()=>api('/api/growth/predictive/alerts').then(setPredictive)).catch(()=>{});
    api('/api/settings/setup-checklist').then(setSetup).catch(()=>{});
  }, []);

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>;

  const todayJobs = data.jobs.filter(j => ['Booked','Dispatched','En Route','On Site','In Progress','Scheduled'].includes(j.status));
  const newLeads = data.contacts.filter(c => c.stage === 'New Lead');
  const openEstimates = data.estimates.filter(e => ['sent','viewed'].includes(e.status));
  const unpaidInvoices = data.invoices.filter(i => ['sent','overdue'].includes(i.status));
  const unpaidTotal = unpaidInvoices.reduce((s,inv) => {
    const lines = inv.lines || [];
    return s + lines.reduce((a,l)=>a + (Number(l.qty)||0)*(Number(l.price)||0), 0) * (1 + (inv.taxRate||7)/100) - (inv.paid||0);
  }, 0);
  const paidThisMonth = data.invoices.filter(i => i.status === 'paid' && i.paidAt && new Date(i.paidAt).getMonth() === new Date().getMonth())
    .reduce((s,i) => s + (i.paid || 0), 0);

  const kpis = [
    { label:'New Leads', value: newLeads.length, color:G.cyan, action: ()=>go('contacts') },
    { label:'Open Estimates', value: openEstimates.length, color:G.yellow, action: ()=>go('estimates') },
    { label:`Active ${cap(plural('visit'))}`, value: todayJobs.length, color:G.green, action: ()=>go('jobs') },
    { label:'Unpaid AR', value: fmtUsd(unpaidTotal), color:G.orange, action: ()=>go('invoices') },
    { label:'Collected This Month', value: fmtUsd(paidThisMonth), color:G.purple, action: ()=>go('invoices') },
    { label:'Team Size', value: '—', color:G.blue, action: ()=>go('team') },
  ];

  return <div style={{ padding:24 }}>
    <div style={{ marginBottom:18 }}>
      <h1 style={{ margin:0, fontSize:28, fontWeight:800 }}>Hey {user.firstName || 'there'}</h1>
      <div style={{ color:G.textMuted, marginTop:4 }}>{user.account?.name} — {user.account?.industry}</div>
    </div>

    {/* Watch + reconcile pinned to the top so the owner sees the actionable
        stuff before vanity metrics. */}
    <Safe name="WatchPanel"><WatchPanel go={go} toast={toast}/></Safe>

    {/* Streamlined 3-step onboarding card. Replaces the old 8-step setup
        checklist. Shows only the 3 highest-leverage actions; auto-hides once
        all are done. Demo data auto-clears once Stripe + Twilio are wired. */}
    <Safe name="QuickStartCard"><QuickStartCard go={go} toast={toast}/></Safe>

    <Safe name="CoachPanel"><CoachPanel go={go} toast={toast}/></Safe>

    <Safe name="PriorityQueuePanel"><PriorityQueuePanel go={go}/></Safe>

    <Safe name="AIActivityPanel"><AIActivityPanel go={go}/></Safe>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14, marginBottom:24 }}>
      {kpis.map(k => (
        <div key={k.label} onClick={k.action} style={{ ...styles.card, cursor:'pointer', borderColor: k.color+'55' }}>
          <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{k.label}</div>
          <div style={{ fontSize:26, fontWeight:800, marginTop:8, color:k.color }}>{k.value}</div>
        </div>
      ))}
    </div>

    {predictive.length > 0 && <div style={{ marginBottom:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <h3 style={{ margin:0, fontSize:16, color:G.purple }}>Revenue Opportunities — Predictive Alerts</h3>
        <Btn sz="sm" onClick={()=>go('growth')}>View all →</Btn>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:12 }}>
        {predictive.slice(0,3).map(p => {
          const c = p.kind === 'replacement' ? G.red : p.kind === 'maintenance' ? G.yellow : G.cyan;
          const label = p.kind === 'replacement' ? '$5-10k REPLACEMENT' : p.kind === 'maintenance' ? '$300+ TUNE-UP' : '$299 MEMBERSHIP';
          return <div key={p.id} style={{ ...styles.card, padding:14, borderColor: c+'66' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
              <strong style={{ fontSize:14 }}>{p.first_name} {p.last_name}</strong>
              <Badge c={c} bg={c+'22'}>{Math.round((p.confidence||0)*100)}%</Badge>
            </div>
            <div style={{ fontSize:12, color:G.textMuted, marginBottom:6 }}>{p.manufacturer} {p.model} · {p.type}</div>
            <div style={{ fontSize:12, color:G.textMuted, lineHeight:1.4, marginBottom:10 }}>{p.reasoning}</div>
            <div style={{ fontSize:11, color: c, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>→ {label}</div>
          </div>;
        })}
      </div>
    </div>}

    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
      <div style={styles.card}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>Today's Jobs</h3>
          <Btn sz="sm" v="ghost" onClick={()=>go('jobs')}>View dispatch board →</Btn>
        </div>
        {todayJobs.length === 0 ? <div style={{ color:G.textMuted, padding:24, textAlign:'center' }}>No {plural('visit')} scheduled. Convert an estimate or create a {cap('visit').toLowerCase()}.</div>
        : todayJobs.slice(0,6).map(j => (
          <div key={j.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:`1px solid ${G.border}` }}>
            <div>
              <div style={{ fontWeight:600 }}>{j.name}</div>
              <div style={{ fontSize:13, color:G.textMuted }}>{j.contact} · {j.address || '—'}</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <Badge c={G.green} bg={G.greenLight}><Dot c={G.green}/> {j.status}</Badge>
              <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>{j.tech || 'Unassigned'} · {fmtUsd(j.est||0)}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>New Leads</h3>
          <Btn sz="sm" v="ghost" onClick={()=>go('contacts')}>View all →</Btn>
        </div>
        {newLeads.length === 0 ? <div style={{ color:G.textMuted, padding:24, textAlign:'center', fontSize:13 }}>No new leads.</div>
        : newLeads.slice(0,5).map(c => (
          <div key={c.id} style={{ padding:'10px 0', borderBottom:`1px solid ${G.border}` }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontWeight:600 }}>{c.firstName} {c.lastName}</div>
              <Badge c={STAGE_COLOR[c.stage]} bg={STAGE_COLOR[c.stage]+'22'}>{c.stage}</Badge>
            </div>
            <div style={{ fontSize:13, color:G.textMuted, marginTop:2 }}>{c.service} · {c.source}</div>
          </div>
        ))}
      </div>
    </div>
  </div>;
}

// Shows the last 24 hours of AI calls + AI SMS replies so owners can see at
// a glance that the agent is actually engaging customers. Click any row to
// jump to the contact (transcripts live in the contact's notes/timeline).
function AIActivityPanel({ go }) {
  const [data, setData] = useState(null);
  useEffect(() => { api('/api/growth/ai-activity?limit=10').then(setData).catch(()=>setData({ calls:[], smsReplies:[], counts:{} })); }, []);
  if (!data) return null;
  const has = (data.calls?.length || 0) + (data.smsReplies?.length || 0) > 0;
  const goContact = (id) => { if (!id) return; window.dispatchEvent(new CustomEvent('tradeops:open-contact', { detail: { contactId: id } })); };
  const fmtAgo = (ts) => {
    if (!ts) return '';
    const ms = Date.now() - new Date(ts).getTime();
    const m = Math.floor(ms/60000); if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`; const h = Math.floor(m/60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h/24)}d ago`;
  };
  return <div style={{ marginBottom:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <h3 style={{ margin:0, fontSize:16, color:G.cyan }}>AI activity {data.counts?.calls7d ? `· ${data.counts.calls7d} call${data.counts.calls7d===1?'':'s'} in 7 days` : ''}</h3>
      {has && <Btn sz="sm" v="ghost" onClick={()=>go('messaging')}>Open Conversations →</Btn>}
    </div>
    {!has ? (
      <div style={{ ...styles.card, padding:18, fontSize:13, color:G.textMuted }}>
        No AI calls or replies yet. Inbound calls to your Twilio number + outbound calls fired from workflows will land here with transcripts.
      </div>
    ) : (
      <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
        {(data.calls || []).slice(0,5).map((c, i) => (
          <div key={c.id} onClick={()=>goContact(c.contact_id)} style={{ padding:'12px 16px', borderTop: i===0?'none':`1px solid ${G.border}`, cursor: c.contact_id?'pointer':'default', display:'flex', gap:14, alignItems:'flex-start' }}>
            <div style={{ width:32, height:32, borderRadius:8, background:G.purpleDim, color:G.purple, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, flexShrink:0 }}>AI</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <strong style={{ fontSize:13 }}>{c.first_name || c.phone || 'Unknown caller'} {c.last_name || ''}</strong>
                <Badge c={c.direction==='outbound'?G.cyan:G.green} bg={(c.direction==='outbound'?G.cyan:G.green)+'22'}>
                  {c.direction==='outbound' ? 'AI called' : 'AI answered'}
                </Badge>
                {c.duration_seconds ? <span style={{ fontSize:11, color:G.textMuted }}>{Math.round(c.duration_seconds/60*10)/10} min</span> : null}
                {c.transcript ? <Badge c={G.purple} bg={G.purpleDim}>transcript</Badge> : null}
                <span style={{ marginLeft:'auto', fontSize:11, color:G.textMuted }}>{fmtAgo(c.created_at)}</span>
              </div>
              {c.summary ? <div style={{ fontSize:12, color:G.textMuted, marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.summary}</div> : null}
              {c.outcome ? <div style={{ fontSize:11, color:G.green, marginTop:2 }}>Outcome: {c.outcome}</div> : null}
            </div>
          </div>
        ))}
        {(data.smsReplies || []).slice(0,3).map((m) => (
          <div key={m.id} onClick={()=>goContact(m.contact_id)} style={{ padding:'12px 16px', borderTop:`1px solid ${G.border}`, cursor: m.contact_id?'pointer':'default', display:'flex', gap:14, alignItems:'flex-start' }}>
            <div style={{ width:32, height:32, borderRadius:8, background:G.cyan+'22', color:G.cyan, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:11, flexShrink:0 }}>SMS</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <strong style={{ fontSize:13 }}>{m.first_name || m.phone || ''} {m.last_name || ''}</strong>
                <Badge c={G.cyan} bg={G.cyan+'22'}>AI replied</Badge>
                <span style={{ marginLeft:'auto', fontSize:11, color:G.textMuted }}>{fmtAgo(m.created_at)}</span>
              </div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.body}</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>;
}

// "What needs me today" — pulls every action-needed signal across CRM into one
// ranked queue. Aggregates new uncontacted leads, stuck jobs, overdue invoices,
// and pending estimates. Tapping a row jumps to the relevant record.
function PriorityQueuePanel({ go }) {
  const [data, setData] = useState(null);
  useEffect(() => { api('/api/growth/priority-queue').then(setData).catch(()=>setData({ items:[], counts:{} })); }, []);
  if (!data) return null;
  const items = data.items || [];
  if (!items.length) return null; // skip the panel entirely when nothing's pending

  const goItem = (it) => {
    if (it.contactId) window.dispatchEvent(new CustomEvent('tradeops:open-contact', { detail: { contactId: it.contactId }}));
    else if (it.type === 'overdue_invoice') go('invoices');
    else if (it.type === 'pending_estimate') go('estimates');
    else if (it.type === 'stuck_job') go('jobs');
  };
  const colorForType = {
    new_lead: G.cyan,
    stuck_job: G.red,
    overdue_invoice: G.orange,
    pending_estimate: G.yellow,
  };
  const fmtAgo = (ts) => {
    if (!ts) return '';
    const ms = Date.now() - new Date(ts).getTime();
    const m = Math.floor(ms/60000); if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`; const h = Math.floor(m/60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h/24)}d ago`;
  };
  const labels = { new_lead: 'New lead', stuck_job: 'Stuck job', overdue_invoice: 'Overdue', pending_estimate: 'Estimate' };
  return <div style={{ marginBottom:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <h3 style={{ margin:0, fontSize:16, color:G.red }}>Today's Priority Queue · {items.length} item{items.length===1?'':'s'}</h3>
    </div>
    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      {items.map((it, i) => {
        const c = colorForType[it.type] || G.textMuted;
        return <div key={i} onClick={()=>goItem(it)} style={{ padding:'12px 16px', borderTop: i===0?'none':`1px solid ${G.border}`, cursor:'pointer', display:'flex', gap:14, alignItems:'flex-start' }}>
          <div style={{ width:80, flexShrink:0 }}>
            <Badge c={c} bg={c+'22'}>{labels[it.type] || it.type}</Badge>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{it.title}</div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{it.body}</div>
          </div>
          <div style={{ fontSize:11, color:G.textMuted, flexShrink:0, alignSelf:'center' }}>{fmtAgo(it.ts)}</div>
        </div>;
      })}
    </div>
  </div>;
}
